import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useConfirm } from 'material-ui-confirm';

import { archiveSystemUser } from '@/api/accounts';
import { paths } from '@/paths';

interface ArchiveSystemUserCardProps {
  accountId: string;
  userId: string;
  userName: string;
}

export function ArchiveSystemUserCard({
  accountId,
  userId,
  userName,
}: ArchiveSystemUserCardProps): React.JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const archiveMutation = useMutation({
    mutationFn: () => archiveSystemUser(accountId, userId),
    onSuccess: () => {
      toast.success('User archived successfully');
      queryClient.invalidateQueries({ queryKey: ['system-users'] });
      navigate(paths.settings.account.systemUsers.index);
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || error?.message || 'Failed to archive user';
      toast.error(message);
    },
  });

  const handleArchive = async () => {
    try {
      await confirm({
        title: 'Archive User',
        description: `${userName} will no longer appear in system user lists. Their data will be preserved and can be restored if needed.`,
        confirmationButtonProps: { color: 'warning' },
        confirmationText: 'Archive',
        cancellationText: 'Cancel',
      });
      archiveMutation.mutate();
    } catch {
      // User cancelled
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
    >
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Archive User
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Typography color="text.secondary" variant="body2">
            Archive this user to remove them from system user lists. Their data is preserved and
            they can be restored later if needed.
          </Typography>
          <Button
            color="warning"
            disabled={archiveMutation.isPending}
            onClick={handleArchive}
            variant="outlined"
          >
            {archiveMutation.isPending ? 'Archiving...' : 'Archive User'}
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
}
